import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth-context';
import { usersAPI, clientsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Users, Trash2, IndianRupee, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '@/lib/api';
import logger from '@/lib/logger';

const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-700',
  staff: 'bg-blue-100 text-blue-700',
  client: 'bg-green-100 text-green-700',
  client_team: 'bg-amber-100 text-amber-700',
};

const ROLE_LABELS = { admin: 'Admin', staff: 'Team', client: 'Client', client_team: 'Client Team' };

const TeamPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'staff', phone: '' });
  const [submitting, setSubmitting] = useState(false);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', role: '', password: '' });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  // Billing access dialog
  const [billingDialogOpen, setBillingDialogOpen] = useState(false);
  const [billingUser, setBillingUser] = useState(null);
  const [billingClientIds, setBillingClientIds] = useState([]);
  const [savingBilling, setSavingBilling] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      const res = await usersAPI.list();
      setUsers(res.data);
    } catch (err) { logger.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadUsers();
    clientsAPI.list()
      .then(res => setClients(res.data || []))
      .catch(() => {});
  }, [loadUsers]); // clientsAPI is stable module-level ref

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await authAPI.register(form);
      toast.success('Team member added');
      setDialogOpen(false);
      setForm({ name: '', email: '', password: '', role: 'staff', phone: '' });
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add member'); }
    setSubmitting(false);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this user?')) return;
    try {
      await usersAPI.delete(userId);
      toast.success('User removed');
      loadUsers();
    } catch (err) { toast.error('Failed to remove user'); }
  };

  const openEditDialog = (u) => {
    setEditUser(u);
    setEditForm({ name: u.name || '', email: u.email || '', phone: u.phone || '', role: u.role || 'staff', password: '' });
    setShowPasswordReset(false);
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editUser) return;
    setEditSubmitting(true);
    try {
      const updates = {};
      if (editForm.name && editForm.name !== editUser.name) updates.name = editForm.name;
      if (editForm.email && editForm.email !== editUser.email) updates.email = editForm.email;
      if (editForm.phone !== (editUser.phone || '')) updates.phone = editForm.phone || null;
      if (editForm.role && editForm.role !== editUser.role) updates.role = editForm.role;
      // IMPORTANT: Only send password when the admin has EXPLICITLY opened the
      // reset-password panel. This prevents browser autofill from silently
      // overwriting a user's password when editing unrelated fields (name,
      // phone, role, email). The `showPasswordReset` flag is the admin's
      // deliberate "yes, I want to reset this user's password" opt-in.
      if (showPasswordReset && editForm.password && editForm.password.length >= 6) {
        updates.password = editForm.password;
      }

      if (Object.keys(updates).length === 0) {
        toast.info('No changes to save');
        setEditDialogOpen(false);
        setEditSubmitting(false);
        return;
      }
      await usersAPI.update(editUser.id, updates);
      toast.success('Team member updated');
      setEditDialogOpen(false);
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update member'); }
    setEditSubmitting(false);
  };

  const openBillingAccess = (u) => {
    setBillingUser(u);
    setBillingClientIds(u.billing_client_ids || []);
    setBillingDialogOpen(true);
  };

  const toggleBillingClient = (clientId) => {
    setBillingClientIds(prev =>
      prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]
    );
  };

  const handleSaveBillingAccess = async () => {
    if (!billingUser) return;
    setSavingBilling(true);
    try {
      await usersAPI.update(billingUser.id, { billing_client_ids: billingClientIds });
      toast.success('Billing access updated');
      setBillingDialogOpen(false);
      loadUsers();
    } catch (err) { toast.error('Failed to update billing access'); }
    setSavingBilling(false);
  };

  if (loading) return <div className="h-64 bg-muted rounded-xl animate-pulse" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold">Team Management</h2>
          <p className="text-sm text-muted-foreground mt-1">{users.length} team members</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-team-member">
              <Plus className="h-4 w-4 mr-2" /> Add Member
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label>Full Name *</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required />
              </div>
              <div>
                <Label>Password *</Label>
                <Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required minLength={6} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="staff">Team</SelectItem>
                      <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="client_team">Client Team</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Adding...' : 'Add Member'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {users.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold mb-1">No team members</h3>
            <p className="text-sm text-muted-foreground">Add your first team member.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="hidden sm:table-cell">Phone</TableHead>
                <TableHead>Billing Access</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map(u => (
                <TableRow key={u.id} data-testid={`team-row-${u.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="text-xs">{u.name?.[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <span className="font-medium text-sm">{u.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                  <TableCell>
                    <Badge className={`text-xs ${ROLE_COLORS[u.role]}`}>{ROLE_LABELS[u.role] || u.role}</Badge>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{u.phone || '-'}</TableCell>
                  <TableCell>
                    {u.role === 'staff' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs gap-1"
                        onClick={() => openBillingAccess(u)}
                        data-testid={`billing-access-${u.id}`}
                      >
                        <IndianRupee className="h-3 w-3" />
                        {(u.billing_client_ids || []).length > 0
                          ? `${u.billing_client_ids.length} client(s)`
                          : 'None'}
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {u.id !== user?.id && (
                        <>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEditDialog(u)} data-testid={`edit-user-${u.id}`} title="Edit member">
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={() => handleDelete(u.id)} data-testid={`delete-user-${u.id}`} title="Delete member">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Edit Member Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Team Member</DialogTitle></DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4" autoComplete="off">
            {/* Autofill honeypot — absorbs aggressive password-manager autofill that
                ignores autoComplete="off" on the real password field below. */}
            <input type="text" name="fake-username" autoComplete="username" style={{ display: 'none' }} tabIndex={-1} aria-hidden="true" />
            <input type="password" name="fake-password" autoComplete="new-password" style={{ display: 'none' }} tabIndex={-1} aria-hidden="true" />
            <div>
              <Label>Full Name *</Label>
              <Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} required data-testid="edit-user-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} required data-testid="edit-user-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Role</Label>
                <Select value={editForm.role} onValueChange={v => setEditForm({...editForm, role: v})}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="staff">Team</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="client_team">Client Team</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} data-testid="edit-user-phone" />
              </div>
            </div>
            <div>
              {!showPasswordReset ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPasswordReset(true)}
                  data-testid="edit-user-reset-password-toggle"
                  className="text-muted-foreground"
                >
                  Reset password
                </Button>
              ) : (
                <div className="space-y-2 border rounded-md p-3 bg-muted/30">
                  <div className="flex items-center justify-between">
                    <Label>New Password <span className="text-xs text-muted-foreground">(min 6 chars)</span></Label>
                    <button
                      type="button"
                      onClick={() => { setShowPasswordReset(false); setEditForm(f => ({ ...f, password: '' })); }}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      Cancel
                    </button>
                  </div>
                  <Input
                    type="password"
                    value={editForm.password}
                    onChange={e => setEditForm({ ...editForm, password: e.target.value })}
                    minLength={6}
                    placeholder="Enter new password..."
                    autoComplete="new-password"
                    data-testid="edit-user-password"
                  />
                </div>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={editSubmitting} data-testid="edit-user-submit">
              {editSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Billing Access Dialog */}
      <Dialog open={billingDialogOpen} onOpenChange={setBillingDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee className="h-5 w-5 text-blue-600" />
              Billing Access — {billingUser?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Select which clients this team member can view pending invoices for on the dashboard.
          </p>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto border rounded-lg p-3">
            {clients.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No clients found</p>
            ) : (
              clients.map(c => (
                <label
                  key={c.id}
                  className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-muted/50 cursor-pointer"
                  data-testid={`billing-client-${c.id}`}
                >
                  <Checkbox
                    checked={billingClientIds.includes(c.id)}
                    onCheckedChange={() => toggleBillingClient(c.id)}
                  />
                  <div>
                    <span className="text-sm font-medium">{c.name}</span>
                    {c.company && <span className="text-xs text-muted-foreground ml-2">({c.company})</span>}
                  </div>
                </label>
              ))
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{billingClientIds.length} client(s) selected</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setBillingDialogOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={handleSaveBillingAccess} disabled={savingBilling} data-testid="save-billing-access">
                {savingBilling ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamPage;
